// HTTP Client with Auth Interceptors
// spec/backend/shared-foundation/api-client.md: shared-foundation-api-client-R02/R03
// spec/backend/access-and-auth/login-sessions.md: access-and-auth-login-sessions-R01/R11

import axios, { CanceledError, type AxiosInstance, type InternalAxiosRequestConfig, type AxiosError, type AxiosResponse } from 'axios';
import { getConfig, isConfigLoaded } from '@/shared/config/runtime';

const DEFAULT_API_BASE_URL = '/api/v1';

/**
 * Получает базовый URL для API
 * Приоритет: runtime config > env variable > default
 */
function getApiBaseUrl(): string {
  // Если runtime конфиг загружен, используем его
  if (isConfigLoaded()) {
    const runtimeConfig = getConfig();
    if (runtimeConfig.apiBaseUrl !== undefined && runtimeConfig.apiBaseUrl.length > 0) {
      return runtimeConfig.apiBaseUrl;
    }
  }

  // Fallback на env variable
  const apiUrl: unknown = import.meta.env['VITE_API_URL'];
  if (typeof apiUrl === 'string' && apiUrl.trim().length > 0) {
    return apiUrl;
  }

  // Fallback на default
  return DEFAULT_API_BASE_URL;
}

// Create axios instance
// Инициализируем с дефолтным URL, будет обновлен после загрузки конфига
export const httpClient: AxiosInstance = axios.create({
  baseURL: DEFAULT_API_BASE_URL,
  timeout: 30000,
  withCredentials: true,
  headers: {
    'Content-Type': 'application/json',
  },
});

/**
 * Обновляет базовый URL после загрузки runtime конфигурации
 * Вызывается автоматически из main.ts после loadConfig()
 */
export function updateApiBaseUrl(): void {
  const baseURL = getApiBaseUrl();
  httpClient.defaults.baseURL = baseURL;
  if (import.meta.env.DEV) {
    console.info(`API base URL set to: ${baseURL}`);
  }
}

/**
 * Cookie сессии передаёт браузер; JS не получает токенов auth.
 * Используется как Axios interceptor-ом, так и fetch-based клиентами.
 */
export function getAuthHeaders(): Record<string, string> { return {}; }

/** sessions-R02/R19/R21: navigation to auth; tokens never enter the browser. */
export function authEndpoint(path: string): string {
  return `${getApiBaseUrl().replace(/\/$/, '')}${path}`;
}
export function redirectToAuth(returnTo: string = `${window.location.pathname}${window.location.search}${window.location.hash}`): void {
  window.location.assign(`${authEndpoint('/auth/oidc/start')}?returnTo=${encodeURIComponent(returnTo)}`);
}

// ============================================================================
// Edit session — сеанс редактирования вкладки, который предъявляют запросы
// ============================================================================

/** Ключ sessionStorage с идентификатором сеанса редактирования вкладки. */
const EDIT_SESSION_ID_STORAGE_KEY = 'editSessionId';

/**
 * Запомнить сеанс редактирования вкладки.
 *
 * Запросы предъявляют сеанс его идентификатором — полем `id` ответа открытия,
 * заголовком X-Edit-Session; идентификатор клиента сеанс не предъявляет
 * (backend-contracts-changelog-undo-redo-api-R05,
 * changelog-undo-redo-edit-sessions-R02, R10). Идентификатор лежит в
 * sessionStorage, чтобы запросы той же вкладки после перезагрузки страницы
 * относились к прежнему сеансу (changelog-undo-redo-edit-sessions-R01).
 */
export function setEditSessionId(id: number): void {
  sessionStorage.setItem(EDIT_SESSION_ID_STORAGE_KEY, String(id));
}

/** Забыть сеанс редактирования вкладки: запросы идут без заголовка сеанса. */
export function clearEditSessionId(): void {
  sessionStorage.removeItem(EDIT_SESSION_ID_STORAGE_KEY);
}

// ============================================================================
// Action Group — groups multiple mutations into a single undo operation
// ============================================================================

/** Имя сведения «группа действия» — одно в запросе и в ответе изменения. */
const ACTION_GROUP_HEADER = 'X-Action-Group-Id';

let actionGroupOpen = false;
let currentActionGroupId: string | null = null;

/**
 * Начать серию запросов одного пользовательского действия.
 *
 * Группу действия заводит продукт: первое изменение серии уходит без группы, и
 * её идентификатор сообщает заголовок ответа X-Action-Group-Id
 * (changelog-undo-redo-changelog-R16). Следующие изменения серии предъявляют
 * эту группу тем же заголовком и присоединяются к ней — вся серия отменяется
 * одним шагом (audit-R07, changelog-undo-redo-changelog-R03). Идентификатор,
 * выдуманный клиентом, группой не является: неизвестная группа не
 * подхватывается, и серия распалась бы на отдельные шаги отмены.
 *
 * Запросы серии идут по одному: следующий отправляется после ответа
 * предыдущего, иначе предъявлять ему ещё нечего.
 */
export function beginActionGroup(): void {
  actionGroupOpen = true;
  currentActionGroupId = null;
}

/** Закончить серию: следующие изменения — самостоятельные действия. */
export function endActionGroup(): void {
  actionGroupOpen = false;
  currentActionGroupId = null;
}

interface ISessionRequestConfig extends InternalAxiosRequestConfig {
  _authSession?: number;
}

let authSessionGeneration = 0;
// Axios merges defaults when the operation is invoked, before the async
// interceptor queue. Capture its generation there without bypassing that queue.
Object.assign(httpClient.defaults, { _authSession: authSessionGeneration });

/** Login/logout boundaries differ even when the same account signs in again. */
export function advanceAuthSession(): number {
  clearEditSessionId();
  endActionGroup();
  authSessionGeneration += 1;
  Object.assign(httpClient.defaults, { _authSession: authSessionGeneration });
  return authSessionGeneration;
}

export function getAuthSessionGeneration(): number {
  return authSessionGeneration;
}

let authenticationFailureHandler: (() => void) | null = null;
export function setAuthenticationFailureHandler(handler: () => void): void {
  authenticationFailureHandler = handler;
}

function assertCurrentSession(generation: number | undefined): void {
  if (generation !== undefined && generation !== authSessionGeneration) {
    throw new CanceledError('Authentication session changed');
  }
}

// Request interceptor: Add Authorization header + X-Edit-Session header + X-Action-Group-Id
httpClient.interceptors.request.use(
  (config: ISessionRequestConfig) => {
    // Preserve the original generation on retries; never rebind an old operation.
    config._authSession ??= authSessionGeneration;
    assertCurrentSession(config._authSession);
    delete config.headers.Authorization;
    config.withCredentials = true;
    const editSessionId = sessionStorage.getItem(EDIT_SESSION_ID_STORAGE_KEY);
    if (editSessionId !== null && editSessionId.length > 0) {
      config.headers['X-Edit-Session'] = editSessionId;
    }
    if (currentActionGroupId !== null) {
      config.headers[ACTION_GROUP_HEADER] = currentActionGroupId;
    }
    return config;
  },
  (error: unknown): never => { throw error; },
);

httpClient.interceptors.response.use(
  (response: AxiosResponse): AxiosResponse => {
    assertCurrentSession((response.config as ISessionRequestConfig)._authSession);
    return response;
  },
  (error: AxiosError): never => {
    const request = error.config as ISessionRequestConfig | undefined;
    assertCurrentSession(request?._authSession);
    if (error.response?.status === 401 && !(request?.url ?? '').startsWith('/auth/')) {
      if (authenticationFailureHandler === null) advanceAuthSession();
      else authenticationFailureHandler();
      redirectToAuth();
    }
    throw error;
  },
);

// ============================================================================
// Mutation callback — allows features to react to successful mutating API calls
// ============================================================================

/** Path segments belonging to changelog endpoints — must NOT trigger mutation callback */
const CHANGELOG_PATH_SEGMENTS = new Set(['edit-sessions', 'undo', 'redo']);

const MUTATING_METHODS = new Set(['post', 'put', 'patch', 'delete']);

type TMutationCallback = () => void;
let mutationCallback: TMutationCallback | null = null;

/**
 * Register a callback invoked after every successful mutating API call
 * (POST/PUT/PATCH/DELETE with X-Edit-Session, excluding changelog endpoints).
 */
export function setMutationCallback(cb: TMutationCallback): void {
  mutationCallback = cb;
}

/** Unregister the mutation callback. */
export function clearMutationCallback(): void {
  mutationCallback = null;
}

function isChangelogUrl(url: string): boolean {
  return url.split('/').some((segment) => CHANGELOG_PATH_SEGMENTS.has(segment));
}

// Response interceptor: notify about successful mutations
httpClient.interceptors.response.use(
  (response: AxiosResponse) => {
    const method = (response.config.method ?? '').toLowerCase();
    const url = response.config.url ?? '';
    const hasEditSession = response.config.headers['X-Edit-Session'] !== undefined;

    // Серия одного действия берёт группу из ответа первого изменения, которое
    // журнал записал (changelog-undo-redo-changelog-R16).
    if (actionGroupOpen && currentActionGroupId === null && MUTATING_METHODS.has(method)) {
      const reported: unknown = (response.headers as Record<string, unknown> | undefined)?.[
        ACTION_GROUP_HEADER.toLowerCase()
      ];
      if (typeof reported === 'string' && reported.length > 0) {
        currentActionGroupId = reported;
      }
    }

    if (
      mutationCallback !== null &&
      hasEditSession &&
      MUTATING_METHODS.has(method) &&
      !isChangelogUrl(url)
    ) {
      mutationCallback();
    }

    return response;
  },
);
